import { useEffect, useRef, useState } from "react";
import { COUNTRY_CODE_OPTIONS } from "../common/country-codes";
import en from "react-phone-number-input/locale/en";
import { GET_TENANT_DETAILS } from "../config/urls";
import { useAuth } from "./AuthProvider";

const DEFAULT_COUNTRY_ISO = "IN";

// One request per tenant, shared by every component that mounts the hook
const tenantCountryRequests = {};

// Tenant details store the country as a name ("United Arab Emirates"); the phone pickers need its ISO code
const getIsoFromCountryName = (countryName) => {
  const name = (countryName || "").trim().toLowerCase();
  return Object.keys(en).find((iso) => iso.length === 2 && en[iso].toLowerCase() === name);
};

const fetchTenantCountryIso = async (getValidToken, tenant) => {
  const token = await getValidToken();
  const response = await fetch(GET_TENANT_DETAILS, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-ID": tenant,
    },
  });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  const data = await response.json();
  return getIsoFromCountryName(data?.results?.[0]?.country) || DEFAULT_COUNTRY_ISO;
};

// ISO code of the tenant's billing country, falling back to India until loaded or if unknown
const useTenantCountryIso = () => {
  const { getValidToken } = useAuth();
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);

  useEffect(() => {
    const tenant = localStorage.getItem("tenant");
    if (!tenantCountryRequests[tenant]) {
      tenantCountryRequests[tenant] = fetchTenantCountryIso(getValidToken, tenant).catch((err) => {
        console.error("Error fetching tenant country:", err);
        delete tenantCountryRequests[tenant];
        return DEFAULT_COUNTRY_ISO;
      });
    }
    let active = true;
    tenantCountryRequests[tenant].then((iso) => active && setCountryIso(iso));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return countryIso;
};

export default useTenantCountryIso;

// Fills the tenant's country (Tenant Settings) into a new form's empty country field.
// A country the user picked, or a saved record's, is never replaced: only an empty field,
// or the value this hook put there itself (refreshed once Tenant Settings load).
// format: "iso" ("IN"), "code" ("91") or "+code" ("+91"); pass enabled=false while editing.
export const useTenantCountryDefault = (value, setValue, { format = "code", enabled = true } = {}) => {
  const countryIso = useTenantCountryIso();
  const applied = useRef(null);

  useEffect(() => {
    if (!enabled || (value && value !== applied.current)) return;
    const option = COUNTRY_CODE_OPTIONS.find((o) => o.iso === countryIso);
    if (!option) return;
    const next = { iso: option.iso, code: option.callingCode, "+code": `+${option.callingCode}` }[format];
    applied.current = next;
    if (next !== value) setValue(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryIso, value, enabled]);
};

// Calling code ("91") of the tenant's country, for values set once when an item is added
export const useTenantCallingCode = () => {
  const countryIso = useTenantCountryIso();
  return COUNTRY_CODE_OPTIONS.find((o) => o.iso === countryIso)?.callingCode || "91";
};
